import type { CSSProperties, ReactNode } from 'react';

import { Icon } from '@/components/Icon';

// S1 Phase 9 (R2 #7) — 지도 URL N/G 아이콘.
// 위치/주소 옆에 표시하는 작은 배지. host 로 네이버/구글/기타 지도 구분.
//   - naver host  → 초록 "N" (aria-label "네이버 지도")
//   - google host → 파랑 "G" (aria-label "구글 지도")
//   - 그 외 URL   → 🔗 (aria-label "외부 지도 링크")
//   - null/빈 값   → 렌더 안 함 (null)
// 항상 외부 링크 (<a target="_blank">) 로 감쌈.
//
// docs/superpowers/plans/2026-05-29-s1-business-ux-comprehensive.md Phase 9

export interface MapUrlIconProps {
  url: string | null;
}

export function MapUrlIcon({ url }: MapUrlIconProps) {
  if (!url) return null;

  let host = '';
  try {
    host = new URL(url).hostname.toLowerCase();
  } catch {
    // invalid URL — 여전히 외부 링크 fallback 으로 노출.
  }

  const isNaver = host.includes('naver');
  const isGoogle =
    host.includes('google.com') || host.includes('goo.gl') || host.includes('maps.app.goo.gl');

  if (isNaver) {
    return (
      <ExtLink url={url} ariaLabel="네이버 지도">
        <Badge color="#03C75A">N</Badge>
      </ExtLink>
    );
  }
  if (isGoogle) {
    return (
      <ExtLink url={url} ariaLabel="구글 지도">
        <Badge color="#4285F4">G</Badge>
      </ExtLink>
    );
  }
  return (
    <ExtLink url={url} ariaLabel="외부 지도 링크">
      <Icon name="link" size={14} aria-hidden />
    </ExtLink>
  );
}

function ExtLink({
  url,
  ariaLabel,
  children,
}: {
  url: string;
  ariaLabel: string;
  children: ReactNode;
}) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={ariaLabel}
      title={ariaLabel}
      style={linkStyle}
    >
      {children}
    </a>
  );
}

function Badge({ color, children }: { color: string; children: ReactNode }) {
  return (
    <span aria-hidden style={{ ...badgeStyle, background: color }}>
      {children}
    </span>
  );
}

const linkStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  textDecoration: 'none',
  lineHeight: 1,
};

const badgeStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 16,
  height: 16,
  borderRadius: 4,
  color: '#fff',
  fontSize: 10,
  fontWeight: 700,
  fontFamily: 'var(--font-mono)',
};
